import { BankOutlined, SearchOutlined } from "@ant-design/icons";
import { Button, Checkbox, Form, Input } from "antd";
import { useForm } from "antd/lib/form/Form";
import { debounce } from "lodash";
import { ReactElement, useEffect, useState } from "react";
import { IBankCheckBoxesList } from "../../../../interfaces/props/IBankCheckBoxesList/IBankCheckBoxesList";
import { useAppSelector } from "../../../../store/reduxHooks";
import { SELECT_BANK_LIST_ACTIVE } from "../../../../store/selectors/banksSelector";
import { IBankForAdd } from "../../../../store/slice/requestAppSlise/requestAppSlise";
import { sortByKey } from "../../../../utils/utilities/sortByKey";
import { BankModalList } from "../../../Application/BankCredit/BankModalList/BankModalList";
import { BankListInApplicationCreate } from ".././BankListInApplicationCreate/BankListInApplicationCreate";

import classes from "./BankCheckBoxesList.module.css";

/**
 * Компонент для отображения списка банков с чекбоксами.
 *
 * @param {Object} props - Свойства компонента.
 * @param {IBankForAdd[]} props.checkedBanksInitial - Исходный список выбранных банков.
 */
export function BankCheckBoxesList({
  checkedBanksInitial,
}: IBankCheckBoxesList): ReactElement {
  const form = Form.useFormInstance();
  const [formSearch] = useForm();
  const bankList = useAppSelector(SELECT_BANK_LIST_ACTIVE);
  const bankListForAdd = bankList.map((bank) => {
    return { id: bank.id, title: bank.title, checked: false };
  });
  const [selectBanks, setSelectBanks] = useState<IBankForAdd[]>([]);
  const [selectBanksId, setSelectBanksId] = useState<number[]>([]);
  const [filterBankListForAdd, setFilterBankListForAdd] =
    useState<IBankForAdd[]>(bankListForAdd);
  const [modalVisible, setModalVisible] = useState(false);

  useEffect(() => {
    const bankIdsInitial = checkedBanksInitial?.map(({ id }) => id);

    setSelectBanks(checkedBanksInitial);
    setSelectBanksId(bankIdsInitial);
  }, [checkedBanksInitial]);

  useEffect(() => {
    form.setFieldValue("banks", selectBanksId);
  }, [form, selectBanksId]);

  function handleCancel() {
    form.setFieldValue("banks", selectBanksId);
    setModalVisible(false);
    formSearch.resetFields();
  }

  function handleOk() {
    const result = form.getFieldValue("banks");

    setModalVisible(false);
    setSelectBanksId(result);
    setSelectBanks(bankListForAdd.filter(({ id }) => result.includes(id)));
    formSearch.resetFields();
  }

  function changeSelectBanks(id: number) {
    setSelectBanks((prev) => prev.filter((bank) => bank.id !== id));
    setSelectBanksId((prev) => prev.filter((value) => value !== id));
  }

  function handleSearch(fields: { search: string }) {
    setFilterBankListForAdd(() => {
      if (!fields.search) return bankListForAdd;

      const searchText = fields.search;

      return bankListForAdd?.filter(({ title }) => {
        return title.toLowerCase().includes(searchText.toLowerCase());
      });
    });
  }

  function openModal() {
    setModalVisible(true);
  }

  const debouncedOnSearch = debounce(handleSearch, 300);

  return (
    <div className={classes.banks}>
      <div className={classes.banks__intro}>
        <div className={classes.banks__title}>
          Предпочтения по отправке в банки
        </div>
        <div className={classes.banks__description}>
          Заявка будет направлена во все банки по умолчанию, либо вы можете
          выбрать желаемые банки из списка доступных
        </div>
        <Button
          className={classes.banks__button}
          size="large"
          onClick={openModal}
        >
          <BankOutlined className={classes.banks__buttonIcon} />
          Выбрать банки
        </Button>
      </div>
      <BankListInApplicationCreate
        bankListForAdd={selectBanks}
        changeSelectBanks={changeSelectBanks}
      />

      <Form.Item name="banks" className={classes.banks__modalWrapper}>
        <Checkbox.Group value={selectBanksId}>
          <BankModalList
            isModalOpen={modalVisible}
            handleCancel={handleCancel}
            handleOk={handleOk}
          >
            <Form
              className={classes.searchForm}
              name="searchPreferedBank"
              onValuesChange={debouncedOnSearch}
              form={formSearch}
            >
              <Form.Item name="search" className={classes.searchNumber}>
                <Input
                  placeholder="Поиск по названию банка"
                  suffix={<SearchOutlined className={classes.searchIcon} />}
                  allowClear
                />
              </Form.Item>
            </Form>

            <div className={classes.bankList}>
              {sortByKey(filterBankListForAdd, "title")?.map((bank) => {
                return (
                  <Checkbox
                    key={bank?.id}
                    value={bank?.id}
                    className={classes.bank}
                  >
                    {bank?.title}
                  </Checkbox>
                );
              })}
            </div>
          </BankModalList>
        </Checkbox.Group>
      </Form.Item>
    </div>
  );
}
